// src/features/game/pages/GamesPage.tsx

import React, {
  useCallback,
  useState,
} from 'react';

import {
  useNavigate,
} from 'react-router-dom';

import {
  useSelector,
} from 'react-redux';

import type {
  RootState,
} from '../../../app/store';

import {
  getTodayDate,
} from '../../../shared/utils/timeUtils';

import RouteConfig from '../../../routes/RouteConfig';

import StoreSelector from '../../store/components/StoreSelector';

import GameCard from '../components/GameCard';
import GameDateSelector from '../components/GameDateSelector';
import GameFilterBar from '../components/GameFilterBar';

import {
  useGames,
} from '../hooks/useGames';

import type {
  GameFilterType,
} from '../hooks/useGames';

import {
  useGameTimer,
} from '../hooks/useGameTimer';

const GamesPage:
  React.FC = () => {
  const navigate =
    useNavigate();

  const authUser =
    useSelector(
      (
        state: RootState,
      ) =>
        state.auth.user,
    );

  const [
    selectedStoreId,
    setSelectedStoreId,
  ] =
    useState<number | null>(
      authUser?.storeId ??
        null,
    );

  const [
    selectedDate,
    setSelectedDate,
  ] =
    useState(
      getTodayDate(),
    );

  const [
    filter,
    setFilter,
  ] =
    useState<GameFilterType>(
      'all',
    );

  const [
    searchTerm,
    setSearchTerm,
  ] = useState('');

  const {
    setGames,
    loading,
    loadGames,
    filteredGames,
  } =
    useGames({
      storeId:
        selectedStoreId,

      selectedDate,

      filter,

      searchTerm,
    });

  const handleRefresh =
    useCallback(() => {
      void loadGames();
    }, [
      loadGames,
    ]);

  useGameTimer({
    setGames,

    onExpired:
      handleRefresh,
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-black sm:text-3xl">
            게임 관리
          </h1>

          <p className="mt-1 text-sm text-gray-600 sm:text-base">
            진행 중인 게임을
            관리합니다.
          </p>
        </div>

        <button
          type="button"
          onClick={() =>
            navigate(
              RouteConfig
                .Management
                .GameCreate
                .fullPath,
            )
          }
          className="rounded-lg bg-main px-6 py-3 font-medium text-white hover:bg-main-medium"
        >
          + 게임 선택
        </button>
      </header>

      {/* Query Controls */}
      <section className="mb-6 flex flex-wrap items-end gap-4">
        <StoreSelector
          selectedStoreId={
            selectedStoreId
          }
          onStoreChange={
            setSelectedStoreId
          }
          label="지점"
        />

        <GameDateSelector
          value={
            selectedDate
          }
          onChange={
            setSelectedDate
          }
        />
      </section>

      {/* Filter */}
      <section className="mb-6">
        <GameFilterBar
          filter={
            filter
          }
          onFilterChange={
            setFilter
          }
          searchTerm={
            searchTerm
          }
          onSearchChange={
            setSearchTerm
          }
        />
      </section>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-20 text-gray-600">
          로딩 중...
        </div>
      ) : filteredGames.length ===
        0 ? (
        <div className="py-20 text-center text-gray-600">
          {selectedStoreId ===
          null
            ? '지점을 선택해주세요.'
            : '게임이 없습니다.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredGames.map(
            (game) => (
              <GameCard
                key={
                  game.playGameId
                }
                game={
                  game
                }
                onRefresh={
                  handleRefresh
                }
              />
            ),
          )}
        </div>
      )}
    </div>
  );
};

export default GamesPage;